/* ///////////////////////////////////////////////////////////////////// */
/* ///////////////////////// CLEANUP REGISTRY ////////////////////////// */
/* ///////////////////////////////////////////////////////////////////// */

/**
 * Registro central de limpieza — "lo que se instala, se desinstala".
 *
 * El sitio reemplaza el contenido de `.transition-wrapper` en cada navegación
 * (ver transitions.js). Todo lo que un módulo instale sobre el DOM que se va a
 * destruir (listeners, observers, instancias de librerías, timers) debe
 * registrarse aquí para poder desmontarse ANTES del reemplazo. De lo contrario
 * los nodos quedan retenidos por closures y se convierten en "nodos zombie".
 *
 * Los recursos que viven en nodos que sobreviven a la transición
 * (`document`, `window`, `body`, `#cursor`) NO deben registrarse aquí:
 * deben instalarse una sola vez con `singleton()`.
 */

/** @type {Set<() => void>} */
const registry = new Set()

/** @type {Map<string, unknown>} */
const singletons = new Map()

/**
 * Registra una función de teardown que se ejecutará antes de la próxima
 * transición de página.
 * @param {() => void} fn Función de limpieza
 */
export function onCleanup (fn) {
  if (typeof fn !== 'function') return
  registry.add(fn)
}

/**
 * Ejecuta y descarta todos los teardowns registrados.
 * Un teardown que falla no impide que corran los demás.
 */
export function runCleanup () {
  for (const fn of registry) {
    try {
      fn()
    } catch (err) {
      console.error('[cleanup] teardown falló:', err)
    }
  }
  registry.clear()
}

/**
 * Añade un listener y registra su remoción automática en el próximo cleanup.
 * Usar sólo para nodos que se destruyen en la transición.
 * @param {EventTarget} target
 * @param {string} type
 * @param {EventListenerOrEventListenerObject} handler
 * @param {boolean|AddEventListenerOptions} [options]
 * @returns {() => void} Función para remover el listener manualmente
 */
export function listen (target, type, handler, options) {
  if (!target) return () => {}

  target.addEventListener(type, handler, options)

  const off = () => target.removeEventListener(type, handler, options)
  onCleanup(off)

  return off
}

/**
 * Crea un recurso una única vez por sesión de página. Las llamadas siguientes
 * devuelven la misma instancia sin volver a instalarla.
 * @template T
 * @param {string} key Identificador único del recurso
 * @param {() => T} factory Constructor, se ejecuta sólo la primera vez
 * @returns {T}
 */
export function singleton (key, factory) {
  if (!singletons.has(key)) {
    singletons.set(key, factory())
  }
  return singletons.get(key)
}

/**
 * Indica si un singleton ya fue creado, sin crearlo.
 * @param {string} key
 * @returns {boolean}
 */
export function hasSingleton (key) {
  return singletons.has(key)
}
